// NOTE: Converted to LF line endings to satisfy ESLint "Delete ␍" complaints.
import { defineStore } from "pinia";
import apiClient from "../services/api.js";

function normalizeBranchCodes(raw) {
  const values = [];

  if (Array.isArray(raw)) {
    values.push(...raw);
  } else if (typeof raw === "string") {
    const s = raw.trim();
    if (s) {
      // Sometimes branch codes arrive as a JSON string: "[\"TSK\",\"ANG\"]"
      if (s.startsWith("[") && s.endsWith("]")) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) values.push(...parsed);
          else values.push(s);
        } catch {
          values.push(s);
        }
      } else {
        // Comma/space separated: "TSK, ANG" / "TSK ANG"
        values.push(...s.split(/[\s,]+/g));
      }
    }
  } else if (raw != null) {
    values.push(raw);
  }

  const out = [];
  for (const v of values) {
    const code = String(v ?? "")
      .trim()
      .toUpperCase();
    if (!code) continue;
    if (!out.includes(code)) out.push(code);
  }
  return out;
}

export const useAuthStore = defineStore("auth", {
  state: () => {
    const storedToken = localStorage.getItem("auth_token") || "";
    const storedRefreshToken = localStorage.getItem("refresh_token") || "";
    if (storedToken) {
      // Rehydrate axios default Authorization header when refreshing the page
      apiClient.defaults.headers.Authorization = `Bearer ${storedToken}`;
    }
    return {
      token: storedToken,
      refreshToken: storedRefreshToken,
      user: null,
      loading: false,
      error: null,
    };
  },
  getters: {
    hasToken: (state) => !!state.token,
    isAuthenticated: (state) => !!state.token && !!state.user,
    username: (state) => state.user?.username || "",
    role: (state) => state.user?.role || "",
    // Be tolerant: backend/users sheet may provide different field names or formats
    // - branchCodes: string[]
    // - branch_codes: string[] | string
    // - branchCode: string (single branch)
    branchCodes: (state) =>
      normalizeBranchCodes(
        state.user?.branchCodes ?? state.user?.branch_codes ?? state.user?.branchCode ?? state.user?.branch_code ?? [],
      ),
  },
  actions: {
    setSession({ token, refreshToken, user }) {
      if (token) {
        this.token = token;
        localStorage.setItem("auth_token", token);
        apiClient.defaults.headers.Authorization = `Bearer ${token}`;
      }
      if (refreshToken) {
        this.refreshToken = refreshToken;
        localStorage.setItem("refresh_token", refreshToken);
      }
      if (user) {
        this.user = user;
      }
    },
    async login(username, password) {
      this.loading = true;
      this.error = null;
      try {
        const res = await apiClient.post("/auth/login", { username, password });
        if (res.data?.token) {
          this.setSession({
            token: res.data.token,
            refreshToken: res.data.refreshToken,
            user: res.data.user || null,
          });
          try {
            const { useBranchesStore } = await import("./branchesStore.js");
            const branchesStore = useBranchesStore();
            await branchesStore.fetch(true);
            const { useActiveBranchStore } = await import("./activeBranchStore.js");
            const activeBranchStore = useActiveBranchStore();
            activeBranchStore.initialized = false;
            await activeBranchStore.init();
          } catch (setupErr) {
            console.warn("[auth] post-login branch setup failed", setupErr);
          }
        } else {
          throw new Error("Invalid login response");
        }
        return true;
      } catch (err) {
        this.error = err?.error || err?.message || "Login failed";
        return false;
      } finally {
        this.loading = false;
      }
    },
    async fetchMe() {
      if (!this.token) return false;
      this.loading = true;
      try {
        const res = await apiClient.get("/auth/me");
        this.user = res.data?.user || this.user;
        this.loading = false;
        return true;
      } catch (err) {
        console.warn("fetchMe failed", err);
        await this.logout();
        this.loading = false;
        return false;
      }
    },
    async logout() {
      this.token = "";
      this.refreshToken = "";
      this.user = null;
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      delete apiClient.defaults.headers.Authorization;
      try {
        const { useActiveBranchStore } = await import("./activeBranchStore.js");
        const activeBranchStore = useActiveBranchStore();
        activeBranchStore.active = "";
        activeBranchStore.initialized = false;
      } catch (err) {
        console.warn("[auth] logout branch reset failed", err);
      }
    },
  },
});
